import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, UploadCloud, ClipboardCheck, FolderKanban, Layers, Users2,
  Building2, Search, ShieldCheck, ScrollText, LogOut, Eye, EyeOff, Menu, X, Sparkles,
} from "lucide-react";

const STAFF_NAV = [
  { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true },
  { to: "/upload", label: "Upload Center", icon: UploadCloud },
  { to: "/review", label: "Review & Publish", icon: ClipboardCheck },
];
const BROWSE_NAV = [
  { to: "/projects", label: "Projects & Topics", icon: FolderKanban },
  { to: "/categories", label: "Categories", icon: Layers },
  { to: "/people", label: "People", icon: Users2 },
  { to: "/organizations", label: "Organizations", icon: Building2 },
  { to: "/search", label: "Search", icon: Search },
];
const ADMIN_NAV = [
  { to: "/users", label: "Users & Roles", icon: ShieldCheck },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive ? "bg-blue-600/15 text-blue-300 border border-blue-500/20" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
        }`
      }
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarContent({ onNav }) {
  const { user, isStaff } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] text-white leading-tight">Work Intelligence</div>
            <div className="text-[11px] text-slate-500 tracking-wide">PORTFOLIO ARCHIVE</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {isStaff && (
          <div className="space-y-1">
            <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600 mb-2">Workspace</div>
            {STAFF_NAV.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
          </div>
        )}
        <div className="space-y-1">
          <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600 mb-2">Explore</div>
          {BROWSE_NAV.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
        </div>
        {user?.role === "admin" && (
          <div className="space-y-1">
            <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600 mb-2">Administration</div>
            {ADMIN_NAV.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
          </div>
        )}
      </nav>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout, isStaff, previewMode, setPreviewMode } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F19]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-white/5 bg-[#070B14] flex-col fixed h-screen z-30">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 bg-[#070B14] border-r border-white/5">
            <button onClick={() => setMobileOpen(false)} className="absolute top-5 right-4 text-slate-400" data-testid="close-mobile-nav"><X className="w-5 h-5" /></button>
            <SidebarContent onNav={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 border-b border-white/5 bg-[#0B0F19]/85 backdrop-blur-xl">
          <div className="flex items-center gap-3 px-4 md:px-8 h-16">
            <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav"><Menu className="w-6 h-6" /></button>
            <form onSubmit={submitSearch} className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  data-testid="global-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ask anything — e.g. 'work related to Lady Gaga'"
                  className="w-full bg-[#070B14] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all"
                />
              </div>
            </form>
            <div className="flex items-center gap-3">
              {isStaff && (
                <button
                  onClick={() => { setPreviewMode(!previewMode); }}
                  data-testid="ceo-preview-toggle"
                  className={`hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${previewMode ? "bg-blue-600/20 text-blue-300 border-blue-500/30" : "text-slate-400 border-white/10 hover:bg-white/5"}`}
                >
                  {previewMode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {previewMode ? "Exit CEO Preview" : "CEO Preview"}
                </button>
              )}
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-sm text-white">{user?.name}</span>
                <span className="text-[11px] uppercase tracking-wider text-blue-400">{previewMode ? "ceo (preview)" : user?.role}</span>
              </div>
              <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors" title="Sign out">
                <LogOut className="w-[18px] h-[18px]" />
              </button>
            </div>
          </div>
          {previewMode && (
            <div className="bg-blue-600/15 border-t border-blue-500/20 px-8 py-1.5 text-center text-xs text-blue-200">
              You are viewing the portfolio exactly as the CEO / Executive Viewer sees it — only published content is shown.
            </div>
          )}
        </header>

        <main key={loc.pathname} className="flex-1 px-4 md:px-8 py-8 max-w-[1400px] w-full mx-auto fade-up">
          {children}
        </main>
      </div>
    </div>
  );
}
